/**
 * /objects — fan-facing object library (物件庫) storefront (v2).
 *
 *   - Browse objects the server permits (scope + front-status filtered;
 *     `backend` rows never arrive). Each card shows a real thumbnail.
 *   - Tag quick-filter chips narrow the grid.
 *   - Tapping a card opens a DETAIL view (preview + description + price +
 *     scope/category); purchase requires an explicit CONFIRM pop-up — no
 *     more one-tap charge.
 *   - Owned objects show 已擁有. `preview`-status objects are shown but
 *     not buyable.
 *
 * The "attach to my character" experience lives on the character page
 * (CharacterMarket / DecorateScene). This page is browse + buy.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { SimpleGlbViewer } from '../components/SimpleGlbViewer';
import {
  listLibraryObjects,
  listMyFanObjects,
  purchaseFanObject,
  type LibraryObject,
} from '../api/fan-api';
import { getStoredFanClaims, fanCognitoConfigured } from '../auth/fan-auth';
import { useDocumentTitle } from '../lib/useDocumentTitle';
import { notifyWalletChanged } from '../lib/wallet-bus';

const SCOPE_LABEL: Record<string, string> = {
  public: '公開', operator: '經營者', character: '角色專屬',
};

export function Objects() {
  useDocumentTitle('物件庫');
  const navigate = useNavigate();
  const signedIn = Boolean(getStoredFanClaims()?.sub);
  const [list, setList] = useState<LibraryObject[] | null>(null);
  const [owned, setOwned] = useState<Set<string> | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [err, setErr] = useState<string | null>(null);
  const [activeTag, setActiveTag] = useState<string | null>(null);
  // Card → detail → confirm flow.
  const [detail, setDetail] = useState<LibraryObject | null>(null);
  const [confirming, setConfirming] = useState(false);
  // Purchase-success "drop into bag" animation; holds the bought object
  // while the modal plays the catch animation, then auto-closes.
  const [justBought, setJustBought] = useState<LibraryObject | null>(null);

  useEffect(() => {
    if (!justBought) return;
    const t = setTimeout(() => { setJustBought(null); setDetail(null); }, 1300);
    return () => clearTimeout(t);
  }, [justBought]);

  useEffect(() => {
    let alive = true;
    void listLibraryObjects()
      .then((r) => { if (alive) setList(r.items); })
      .catch((e) => { if (alive) setErr((e as Error).message); });
    if (signedIn) {
      void listMyFanObjects()
        .then((r) => { if (alive) setOwned(new Set(r.items.map((it) => it.objectId))); })
        .catch(() => { /* ignore */ });
    } else {
      setOwned(new Set());
    }
    return () => { alive = false; };
  }, [signedIn]);

  // Tag chips derived from the visible objects.
  const allTags = useMemo(() => {
    const s = new Set<string>();
    (list ?? []).forEach((o) => (o.tags ?? []).forEach((t) => s.add(t)));
    return [...s].sort();
  }, [list]);

  const visible = useMemo(() => {
    const items = list ?? [];
    return activeTag ? items.filter((o) => (o.tags ?? []).includes(activeTag)) : items;
  }, [list, activeTag]);

  async function confirmBuy(obj: LibraryObject): Promise<void> {
    if (!signedIn) {
      if (fanCognitoConfigured()) navigate('/login');
      return;
    }
    if (busyId) return;
    setBusyId(obj.objectId);
    setErr(null);
    try {
      const r = await purchaseFanObject(obj.objectId, { walletScope: 'platform' });
      if (r.purchased || r.alreadyOwned) {
        setOwned((prev) => new Set([...(prev ?? []), obj.objectId]));
      }
      if (r.purchased) notifyWalletChanged();
      setConfirming(false);
      if (r.purchased) {
        // Keep the modal open for the drop-into-bag animation; the
        // justBought effect closes it after the bag catches the item.
        setJustBought(obj);
      } else {
        setDetail(null);
      }
    } catch (e) {
      setErr((e as Error).message);
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="max-w-6xl mx-auto px-3 sm:px-4 py-4 sm:py-6 space-y-4">
      <header>
        <div className="text-[11px] uppercase tracking-[0.2em] text-blue-400">物件庫</div>
        <h1 className="text-xl sm:text-2xl font-semibold mt-1">虛擬商品</h1>
        <p className="text-xs sm:text-sm text-slate-400 mt-1">
          購買後可套用在你擁有的任何角色身上。所有經營者點數與平台點 1:1 通用。
        </p>
      </header>

      {err && (
        <div role="alert" className="rounded-lg border border-rose-500/40 bg-rose-500/10 p-3 text-sm text-rose-200">{err}</div>
      )}

      {/* Tag quick-filter chips */}
      {allTags.length > 0 && (
        <div className="flex flex-wrap gap-1.5">
          <button
            type="button"
            onClick={() => setActiveTag(null)}
            className={'rounded-full px-3 py-1 text-xs transition ' + (activeTag === null ? 'bg-violet-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700')}
          >全部</button>
          {allTags.map((t) => (
            <button
              key={t}
              type="button"
              onClick={() => setActiveTag((cur) => (cur === t ? null : t))}
              className={'rounded-full px-3 py-1 text-xs transition ' + (activeTag === t ? 'bg-violet-500 text-white' : 'bg-slate-800 text-slate-300 hover:bg-slate-700')}
            >#{t}</button>
          ))}
        </div>
      )}

      {!list && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3" aria-hidden="true">
          {Array.from({ length: 8 }).map((_, i) => (
            <div key={i} className="rounded-xl border border-slate-800 bg-slate-900/60 overflow-hidden flex flex-col">
              <div className="aspect-square nx-skeleton-shimmer" />
              <div className="p-2.5 sm:p-3 flex-1 flex flex-col gap-2">
                <div className="nx-skeleton-shimmer h-3.5 w-3/4 rounded" />
                <div className="nx-skeleton-shimmer h-2.5 w-1/2 rounded" />
              </div>
            </div>
          ))}
        </div>
      )}

      {list && visible.length === 0 && (
        <div className="rounded-xl border border-dashed border-slate-700 bg-slate-900/40 p-10 text-center text-slate-400 text-sm">
          <div className="text-4xl mb-2 opacity-70" aria-hidden="true">📦</div>
          <div>{activeTag ? '此分類沒有物件。' : '目前沒有可顯示的物件。'}</div>
        </div>
      )}

      {list && visible.length > 0 && (
        <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-4 gap-3">
          {visible.map((obj, i) => {
            const isOwned = !!owned?.has(obj.objectId);
            const buyable = obj.frontStatus === 'listed';
            return (
              <button
                key={obj.objectId}
                type="button"
                onClick={() => { setDetail(obj); setConfirming(false); }}
                style={{ ['--enter-delay' as string]: `${Math.min(i, 11) * 0.045}s` }}
                className={
                  'nx-card-enter text-left group rounded-xl border bg-slate-900/60 overflow-hidden flex flex-col transition duration-200 ' +
                  (isOwned
                    ? 'border-blue-500/30 shadow-[0_0_24px_-12px_rgba(96,165,250,0.4)]'
                    // Altitude illusion: the card lifts 1px higher while its
                    // shadow drops LOWER and spreads (16px offset) — the gap
                    // between object and shadow is what reads as height.
                    : 'border-slate-800 hover:border-violet-500/40 hover:-translate-y-1 hover:shadow-[0_16px_36px_-12px_rgba(167,139,250,0.5)]')
                }
              >
                <div className="aspect-square bg-slate-950 relative overflow-hidden">
                  <ObjectThumb obj={obj} hoverZoom />
                  {isOwned && (
                    <span className="absolute top-1.5 right-1.5 rounded-full bg-blue-500/80 px-2 py-0.5 text-[10px] font-medium text-white">已擁有</span>
                  )}
                  {!buyable && !isOwned && (
                    <span className="absolute top-1.5 left-1.5 rounded-full bg-slate-800/80 px-2 py-0.5 text-[10px] text-slate-300">展示</span>
                  )}
                </div>
                <div className="p-2.5 sm:p-3 flex-1 flex flex-col gap-1.5">
                  <div className="font-medium text-slate-100 text-sm truncate" title={obj.label}>{obj.label}</div>
                  <div className="flex items-center justify-between gap-2">
                    {buyable ? (
                      <span className="text-amber-300 text-xs tabular-nums">{obj.pricePoints} 點</span>
                    ) : (
                      <span className="text-slate-500 text-[11px]">僅展示</span>
                    )}
                    {obj.tags && obj.tags.length > 0 && (
                      <span className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-400 truncate max-w-[60%]">#{obj.tags[0]}</span>
                    )}
                  </div>
                </div>
              </button>
            );
          })}
        </div>
      )}

      {/* Detail → confirm modal */}
      {detail && (
        <div className="fixed inset-0 z-50 grid place-items-center bg-black/70 p-4" onClick={() => { if (!busyId && !justBought) { setDetail(null); setConfirming(false); } }}>
          <div className="relative w-full max-w-md rounded-2xl border border-slate-800 bg-slate-900 overflow-hidden" onClick={(e) => e.stopPropagation()}>
            {/* Purchase-success overlay — the object falls (gravity ease-in,
                shrinking + tumbling) into a bag that squashes on catch. */}
            {justBought && (
              <div className="absolute inset-0 z-10 bg-slate-950/85 flex flex-col items-center justify-end pb-8 overflow-hidden" aria-live="polite">
                <div className="nx-drop-item absolute top-12 left-1/2 h-24 w-24 -translate-x-1/2">
                  {justBought.thumbnail ? (
                    <img src={justBought.thumbnail} alt="" className="h-full w-full object-contain drop-shadow-[0_8px_16px_rgba(0,0,0,0.5)]" />
                  ) : (
                    <div className="h-full w-full flex items-center justify-center text-6xl" aria-hidden="true">📦</div>
                  )}
                </div>
                <div className="nx-bag-catch text-6xl" aria-hidden="true">🎒</div>
                <div className="nx-drop-label mt-3 text-sm font-medium text-emerald-300">已放入背包 ✓</div>
              </div>
            )}
            <div className="aspect-square bg-slate-950 relative">
              {/* object-library-v2: live 3D preview in the detail pop-up
                  (GLB only — SimpleGlbViewer uses GLTFLoader). Non-GLB or
                  missing model → thumbnail / 📦 fallback. */}
              {detail.modelFormat === 'glb' && detail.modelPath ? (
                <>
                  <SimpleGlbViewer
                    key={detail.objectId}
                    url={detail.modelPath}
                    framePadding={0.2}
                    className="absolute inset-0 h-full w-full"
                    interactive
                  />
                  <span className="pointer-events-none absolute bottom-2 left-1/2 -translate-x-1/2 rounded-full bg-slate-950/60 px-2.5 py-1 text-[10px] text-slate-300 backdrop-blur">
                    ↔ 拖曳旋轉檢視
                  </span>
                </>
              ) : (
                <ObjectThumb obj={detail} large />
              )}
            </div>
            <div className="p-4 space-y-3">
              <div className="flex items-start justify-between gap-2">
                <h2 className="text-lg font-semibold text-slate-100">{detail.label}</h2>
                <span className="shrink-0 rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-300">
                  {SCOPE_LABEL[detail.scope ?? 'public'] ?? '公開'}
                </span>
              </div>
              {detail.description && <p className="text-sm text-slate-400 whitespace-pre-wrap">{detail.description}</p>}
              {detail.tags && detail.tags.length > 0 && (
                <div className="flex flex-wrap gap-1">
                  {detail.tags.map((t) => (
                    <span key={t} className="rounded-full bg-slate-800 px-2 py-0.5 text-[10px] text-slate-400">#{t}</span>
                  ))}
                </div>
              )}
              <div className="flex items-center justify-between pt-1">
                {detail.frontStatus === 'listed'
                  ? <span className="text-amber-300 text-base font-semibold tabular-nums">{detail.pricePoints} 點</span>
                  : <span className="text-slate-500 text-sm">此物件僅供展示,暫不開放購買</span>}
              </div>

              {/* Action area */}
              {owned?.has(detail.objectId) ? (
                <div className="w-full rounded-lg bg-blue-500/15 border border-blue-500/30 text-blue-300 text-sm py-2.5 text-center font-medium">已擁有</div>
              ) : detail.frontStatus !== 'listed' ? (
                <button type="button" onClick={() => { setDetail(null); setConfirming(false); }} className="w-full rounded-lg bg-slate-800 text-slate-300 text-sm py-2.5">關閉</button>
              ) : !confirming ? (
                <button
                  type="button"
                  onClick={() => setConfirming(true)}
                  className="w-full rounded-lg bg-violet-500 hover:bg-violet-400 text-white text-sm font-medium py-2.5 transition active:scale-[0.98]"
                >購買 · {detail.pricePoints} 點</button>
              ) : (
                <div className="space-y-2 rounded-lg border border-violet-500/40 bg-violet-500/10 p-3">
                  <p className="text-sm text-violet-100">確認用 <b className="tabular-nums">{detail.pricePoints}</b> 點購買「{detail.label}」?</p>
                  <div className="flex gap-2">
                    <button
                      type="button"
                      disabled={busyId === detail.objectId}
                      onClick={() => { void confirmBuy(detail); }}
                      className="flex-1 rounded-lg bg-violet-500 hover:bg-violet-400 disabled:bg-slate-700 disabled:text-slate-500 text-white text-sm font-medium py-2 transition"
                    >{busyId === detail.objectId ? '處理中…' : '確認購買'}</button>
                    <button
                      type="button"
                      disabled={busyId === detail.objectId}
                      onClick={() => setConfirming(false)}
                      className="rounded-lg border border-slate-700 px-4 text-sm text-slate-300"
                    >取消</button>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

/** Thumbnail with placeholder fallback (fixes the v1 📦-always bug).
 *  `hoverZoom` — gentle 1.06x scale on the parent button's hover
 *  (group-hover), paired with the card's lift+shadow for a tactile
 *  "pick it up off the shelf" read. Parent must be overflow-hidden. */
function ObjectThumb({ obj, large, hoverZoom }: { obj: LibraryObject; large?: boolean; hoverZoom?: boolean }) {
  const [failed, setFailed] = useState(false);
  if (obj.thumbnail && !failed) {
    return (
      <img
        src={obj.thumbnail}
        alt={obj.label}
        loading="lazy"
        onError={() => setFailed(true)}
        className={
          'absolute inset-0 h-full w-full object-contain'
          + (hoverZoom ? ' transition-transform duration-300 ease-out group-hover:scale-[1.06] motion-reduce:transition-none motion-reduce:group-hover:scale-100' : '')
        }
      />
    );
  }
  return (
    <div className={'absolute inset-0 flex items-center justify-center text-slate-700 ' + (large ? 'text-7xl' : 'text-5xl')} aria-hidden="true">📦</div>
  );
}
